import React, { useState } from 'react';
import { Network, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

interface MultiJoinNodeConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
    inputColumns: any[];
    upstreamNodes?: Record<string, string>; // Map of handleId -> Node Name
}

export const MultiJoinNodeConfig: React.FC<MultiJoinNodeConfigProps> = ({ data, onChange, inputColumns, upstreamNodes = {} }) => {
    // Config Structure:
    // joins: [
    //   { right_index: 1, type: 'LEFT', conditions: [{leftCol: 'id', rightCol: 'fk'}] },
    //   { right_index: 2, type: 'INNER', conditions: [...] } 
    // ]

    const joins = data.joins || [];

    // Helper to get columns for a specific handle
    const getColsForHandle = (handleId: string) => {
        return inputColumns?.filter((c: any) => c.sourceHandle === handleId || (handleId === 'input-0' && c.sourceHandle === 'default')) || [];
    };

    const centerCols = getColsForHandle('input-0');

    const updateJoin = (index: number, field: string, value: any) => {
        const newJoins = [...joins];
        if (!newJoins[index]) {
            newJoins[index] = { right_index: index + 1, type: 'LEFT', conditions: [] };
        }
        newJoins[index] = { ...newJoins[index], [field]: value };
        onChange('joins', newJoins);
    };

    const addCondition = (joinIndex: number) => {
        const newJoins = [...joins];
        if (!newJoins[joinIndex]) {
            newJoins[joinIndex] = { right_index: joinIndex + 1, type: 'LEFT', conditions: [] };
        }
        const currentConds = newJoins[joinIndex].conditions || [];
        newJoins[joinIndex].conditions = [...currentConds, { leftCol: '', rightCol: '' }];
        onChange('joins', newJoins);
    };

    const updateCondition = (joinIndex: number, condIndex: number, field: string, value: string) => {
        const newJoins = [...joins];
        newJoins[joinIndex].conditions[condIndex] = {
            ...newJoins[joinIndex].conditions[condIndex],
            [field]: value
        };
        onChange('joins', newJoins);
    };

    const removeCondition = (joinIndex: number, condIndex: number) => {
        const newJoins = [...joins];
        newJoins[joinIndex].conditions = newJoins[joinIndex].conditions.filter((_: any, i: number) => i !== condIndex);
        onChange('joins', newJoins);
    };

    return (
        <div className="space-y-4">
            <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100 text-xs text-indigo-800 flex gap-2">
                <Network className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                    <strong>Star Join:</strong> joins input tables (1-4) against the Center Table (0).
                </div>
            </div>

            {/* Render 4 potential joins */}
            {[0, 1, 2, 3].map((i) => {
                const tableIdx = i + 1;
                const joinConfig = joins[i] || { right_index: tableIdx, type: 'LEFT', conditions: [] };
                const rightCols = getColsForHandle(`input-${tableIdx}`);
                const hasInput = rightCols.length > 0;

                // Get name for this input
                const inputName = upstreamNodes[`input-${tableIdx}`] || `Input ${tableIdx}`;
                const centerName = upstreamNodes['input-0'] || 'Center Table';

                return (
                    <div key={tableIdx} className={`border rounded-md bg-white p-3 space-y-3 ${!hasInput ? 'opacity-70' : ''}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                                <span className="text-xs font-bold text-gray-700 flex items-center gap-2">
                                    Join {centerName}
                                    {!hasInput && <span className="text-[9px] font-normal text-red-500">(Not Connected)</span>}
                                </span>
                                <span className="text-[10px] text-gray-400">with {inputName}</span>
                            </div>
                            <Select
                                value={joinConfig.type}
                                onValueChange={(v) => updateJoin(i, 'type', v)}
                                disabled={!hasInput}
                            >
                                <SelectTrigger className="h-6 w-24 text-[10px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="LEFT">Left Join</SelectItem>
                                    <SelectItem value="INNER">Inner Join</SelectItem>
                                    <SelectItem value="RIGHT">Right Join</SelectItem>
                                    <SelectItem value="FULL">Full Join</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Conditions */}
                        {hasInput && (
                            <div className="space-y-2">
                                {joinConfig.conditions?.map((cond: any, cIdx: number) => (
                                    <div key={cIdx} className="flex items-center gap-2">
                                        {/* Left Col (Center) */}
                                        <div className="flex-1 min-w-0">
                                            <Select
                                                value={cond.leftCol}
                                                onValueChange={(v) => updateCondition(i, cIdx, 'leftCol', v)}
                                            >
                                                <SelectTrigger className="h-6 text-[10px] w-full px-1">
                                                    <SelectValue placeholder={`${centerName} Col`} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {centerCols.map((c: any) => (
                                                        <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <span className="text-gray-400">=</span>

                                        {/* Right Col (Table X) */}
                                        <div className="flex-1 min-w-0">
                                            <Select
                                                value={cond.rightCol}
                                                onValueChange={(v) => updateCondition(i, cIdx, 'rightCol', v)}
                                            >
                                                <SelectTrigger className="h-6 text-[10px] w-full px-1">
                                                    <SelectValue placeholder={`${inputName} Col`} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {rightCols.map((c: any) => (
                                                        <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <button onClick={() => removeCondition(i, cIdx)} className="text-gray-400 hover:text-red-500 shrink-0">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}

                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full h-6 text-[10px] border-dashed text-gray-400"
                                    onClick={() => addCondition(i)}
                                >
                                    <Plus className="h-3 w-3 mr-1" /> Add Condition
                                </Button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
